import { loadControlRoute } from "@/utils/control-route-preload"
import { preloadProvidersData } from "@/hooks/use-providers"
import { preloadCapabilityLibrary } from "@/context/capability-library"
import { useLocation } from "@solidjs/router"
import { createEffect, onCleanup } from "solid-js"

// Only routes served by their own chunk are worth prefetching. The dashboard
// layout, session route, and session providers ship in the entry bundle, so
// they are not prefetched.
export type RoutePrefetchDependencies = {
  loadControlRoute: typeof loadControlRoute
  loadSessionPage: () => Promise<unknown>
  loadMachines: () => Promise<unknown>
  preloadProviders: (signal: AbortSignal) => Promise<unknown>
  preloadTools: (signal: AbortSignal) => Promise<unknown>
}

const defaultDependencies: RoutePrefetchDependencies = {
  loadControlRoute,
  loadSessionPage: () => import("@/pages/session"),
  loadMachines: () => import("@/pages/machines"),
  preloadProviders: (signal) => preloadProvidersData({ signal }),
  preloadTools: (signal) => preloadCapabilityLibrary({ signal }),
}

export function createRoutePrefetchController(
  path: string,
  dependencies: RoutePrefetchDependencies = defaultDependencies,
) {
  const abort = new AbortController()
  const ignoreFailure = (request: Promise<unknown>) => void request.catch(() => undefined)

  if (path === "/dashboard/providers") {
    ignoreFailure(dependencies.loadControlRoute("providers"))
    ignoreFailure(dependencies.preloadProviders(abort.signal))
  } else if (path === "/dashboard/tools") {
    ignoreFailure(dependencies.loadControlRoute("tools"))
    ignoreFailure(dependencies.preloadTools(abort.signal))
  } else if (path === "/dashboard/settings") {
    ignoreFailure(dependencies.loadControlRoute("settings"))
  } else if (path === "/dashboard/machines") {
    ignoreFailure(dependencies.loadMachines())
  } else if (path.startsWith("/dashboard/m/")) {
    ignoreFailure(dependencies.loadSessionPage())
  }

  return { dispose: () => abort.abort() }
}

export function RoutePrefetchController() {
  const location = useLocation()
  createEffect(() => {
    const controller = createRoutePrefetchController(location.pathname)
    onCleanup(controller.dispose)
  })
  return null
}
